import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select } from '@/components/Select'
import {
  PRIORITY_SOURCE_DESCRIPTIONS,
  PRIORITY_SOURCE_LABELS,
  type PriorityEntryDraft,
  type PrioritySource,
} from '../priorityPathUtils'
import { documentTypeLabel } from '../labels'

const CHOOSABLE_SOURCES: PrioritySource[] = ['document_field', 'output_field', 'request_case_data']

export interface PriorityEntryEditorProps {
  /** 1-based rank, shown as an explicit order badge - the tiny move up/down
   * arrows alone left order ambiguous at a glance, especially with 3+
   * entries (direct operator feedback: "so confusing"). */
  rank: number
  entry: PriorityEntryDraft
  onChange: (entry: PriorityEntryDraft) => void
  onRemove: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  documentFieldOptions: string[]
  outputFieldOptions: string[]
}

// One row of the Priority list - a friendly {source, value} pair, never a
// raw "extracted.*.field" string the operator has to type by hand. Options
// for "This document's field" and "Another output field" are sourced from
// real, already-known field names (the product's required document types,
// and this product's other output_resolution entries), so a value can only
// ever be one that actually exists.
export function PriorityEntryEditor({
  rank,
  entry,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  documentFieldOptions,
  outputFieldOptions,
}: PriorityEntryEditorProps) {
  // Never offer a source that has nothing to pick from - selecting
  // "Another output field" with zero other priority entries above/below
  // used to leave `value` empty, which serializes to a bare "data." with
  // nothing after the dot. That string doesn't match any of the 3
  // recognized forms, so it silently round-trips back in as 'raw' - a
  // dead end, since 'raw' used to be locked and unrenamable. Filtering the
  // choice out here stops the broken value from ever being created.
  const usableSources = CHOOSABLE_SOURCES.filter((source) => {
    if (source === 'document_field') return documentFieldOptions.length > 0
    if (source === 'output_field') return outputFieldOptions.length > 0
    return true
  })
  const sourceOptions = (entry.source === 'raw' ? (['raw', ...usableSources] as PrioritySource[]) : usableSources).map(
    (source) => ({ value: source, label: PRIORITY_SOURCE_LABELS[source] }),
  )

  function defaultValueFor(source: PrioritySource): string {
    if (source === 'document_field') return documentFieldOptions[0] ?? ''
    if (source === 'output_field') return outputFieldOptions[0] ?? ''
    return ''
  }

  return (
    <div className="flex items-center gap-2 rounded-control border border-border bg-bg p-2">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-raised text-meta font-semibold text-text-secondary"
        aria-label={`Priority ${rank}`}
      >
        {rank}
      </span>
      <div className="flex flex-col">
        <button
          type="button"
          aria-label="Move up (tried earlier)"
          disabled={!onMoveUp}
          onClick={onMoveUp}
          className="text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronUp size={14} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          aria-label="Move down (tried later)"
          disabled={!onMoveDown}
          onClick={onMoveDown}
          className="text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ChevronDown size={14} strokeWidth={1.5} />
        </button>
      </div>

      <Select
        value={entry.source}
        onChange={(source) => onChange({ source: source as PrioritySource, value: defaultValueFor(source as PrioritySource) })}
        options={sourceOptions}
        className="w-56 shrink-0"
      />

      {entry.source === 'document_field' && (
        <Select
          value={entry.value}
          onChange={(value) => onChange({ source: 'document_field', value })}
          options={documentFieldOptions.map((field) => ({ value: field, label: documentTypeLabel(field) }))}
          className="flex-1"
        />
      )}

      {entry.source === 'output_field' && (
        <Select
          value={entry.value}
          onChange={(value) => onChange({ source: 'output_field', value })}
          options={outputFieldOptions.map((field) => ({ value: field, label: field }))}
          className="flex-1"
        />
      )}

      {entry.source === 'request_case_data' && (
        <input
          type="text"
          value={entry.value}
          onChange={(event) => onChange({ source: 'request_case_data', value: event.target.value })}
          placeholder="Case-data key name"
          className="flex-1 rounded-control border border-border bg-surface px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
      )}

      {entry.source === 'raw' && (
        <input
          type="text"
          value={entry.value}
          onChange={(event) => onChange({ source: 'raw', value: event.target.value })}
          placeholder="e.g. data.some_field"
          className="flex-1 rounded-control border border-border bg-surface px-3 py-2 font-mono text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
      )}

      <InfoTooltip text={PRIORITY_SOURCE_DESCRIPTIONS[entry.source]} />

      <button
        type="button"
        aria-label="Remove this priority"
        onClick={onRemove}
        className="shrink-0 text-text-secondary hover:text-danger"
      >
        <X size={14} strokeWidth={1.5} />
      </button>
    </div>
  )
}
